/**
 * Pure mappers: HotelsRiver DP marketing offer → vacation-package quote PDF fields.
 * Kept free of DOM / localStorage imports so unit tests stay lightweight.
 */

import { formatIsraelDisplayDate } from "../DatesHelpers";

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Parse "TLV → HER" / "TLV -> HER" style route lines from marketing offer VM.
 * @param {string|null|undefined} routeLine
 * @returns {{ dep: string, arr: string }}
 */
export function parseMarketingOfferRouteLine(routeLine) {
  const raw = String(routeLine || "").trim();
  if (!raw) return { dep: "", arr: "" };
  const parts = raw.split(/\s*(?:→|->|–|—)\s*/);
  if (parts.length >= 2) {
    return { dep: parts[0].trim(), arr: parts.slice(1).join(" → ").trim() };
  }
  return { dep: raw, arr: "" };
}

/**
 * Split "08:00 – 12:30" time ranges into dep/arr times.
 * @param {string|null|undefined} timeRange
 * @returns {{ depTime: string, arrTime: string }}
 */
export function parseMarketingOfferTimeRange(timeRange) {
  const raw = String(timeRange || "").trim();
  if (!raw) return { depTime: "", arrTime: "" };
  const parts = raw.split(/\s*[–—\-]\s*/);
  if (parts.length >= 2) {
    return { depTime: parts[0].trim(), arrTime: parts[1].trim() };
  }
  return { depTime: raw, arrTime: "" };
}

function displayDateFromVm(value) {
  return formatIsraelDisplayDate(value);
}

/**
 * SSOT payment total for HotelsRiver DP quote PDF — must match sidebar סה״כ לתשלום.
 *
 * `packageTotalIlsForDisplay` is the all-in sidebar total (floored flight+hotel
 * package + handling / commission, and baggage when lean includes it). Prefer it.
 * Never use hotel-only `packagePriceRaw` / supplier USD / marketing amountDisplay.
 *
 * When only a package base is available, compose base + handling (+ CC when non-lean).
 *
 * @param {{
 *   packageTotalIlsForDisplay?: number|null,
 *   packageBaseIls?: number|null,
 *   handlingFeeIls?: number|null,
 *   creditCardFeeIls?: number|null,
 *   leanMode?: boolean,
 *   pricingVmFinalTotalIls?: number|null,
 * }} [input]
 * @returns {number|null}
 */
export function resolveHotelsRiverPackageQuotePdfTotalIls(input = {}) {
  const {
    packageTotalIlsForDisplay = null,
    packageBaseIls = null,
    handlingFeeIls = 0,
    creditCardFeeIls = 0,
    leanMode = false,
    pricingVmFinalTotalIls = null,
  } = input;

  // Sidebar סה״כ לתשלום — already all-in. Do not add fees again.
  const displayTotal = Number(packageTotalIlsForDisplay);
  if (Number.isFinite(displayTotal) && displayTotal > 0) {
    return Math.round(displayTotal);
  }

  // Lean / pricing VM final is also all-in (card + handling [+ baggage]).
  const vmFinal = Number(pricingVmFinalTotalIls);
  if (Number.isFinite(vmFinal) && vmFinal > 0) {
    return Math.round(vmFinal);
  }

  // Compose from package base + fees when display total is unavailable.
  const base = Number(packageBaseIls);
  if (Number.isFinite(base) && base > 0) {
    const hf = Number(handlingFeeIls);
    const cc = leanMode ? 0 : Number(creditCardFeeIls);
    return Math.round(
      base +
        (Number.isFinite(hf) && hf > 0 ? hf : 0) +
        (Number.isFinite(cc) && cc > 0 ? cc : 0)
    );
  }

  return null;
}

/**
 * Hebrew type label for a checked LDS baggage supplement (vacation-package PDF pattern).
 * @param {object} supp
 * @returns {string}
 */
function hebrewBaggageTypeLabel(supp) {
  const type = String(supp?.type || supp?.orig?.supp?.type || "").toUpperCase();
  if (type === "LUGGAGE" || type === "BAGGAGE") return "מזוודות";
  if (type === "TROLLEY") return "תיקי יד";
  const desc = String(
    supp?.description || supp?.desc || supp?.orig?.supp?.desc || ""
  ).trim();
  return desc || "כבודה";
}

/**
 * Weight text from supplement row / orig params.
 * @param {object} supp
 * @returns {string}
 */
function baggageWeightText(supp) {
  if (supp?.weightText) return String(supp.weightText).trim();
  if (supp?.weight && supp?.unit) {
    return `${supp.weight}${supp.unit}`;
  }
  const params = supp?.orig?.supp?.params;
  if (Array.isArray(params)) {
    const weightParam = params.find((p) => p?.name === "weight");
    const unitParam = params.find((p) => p?.name === "unit");
    if (weightParam?.value != null && unitParam?.value != null) {
      return `${weightParam.value}${unitParam.value}`;
    }
  }
  return "";
}

/**
 * Optional "(TLV → ATH)" suffix from supplement flight metadata.
 * @param {object} supp
 * @returns {string}
 */
function baggageFlightDirectionSuffix(supp) {
  const flight = supp?.flight || supp?.orig?.flight;
  if (!flight) return "";
  const leg = Array.isArray(flight) ? flight[0] : flight;
  const depCity = leg?.depCity || "";
  const arrCity = leg?.arrCity || "";
  if (depCity && arrCity) return ` (${depCity} → ${arrCity})`;
  return "";
}

/**
 * Selected paid baggage → vacation-package PDF `supplementsList` (תוספות section).
 * Same shape as Belvedere / PackageDetails: `{ name, priceIls }`.
 *
 * @param {object|null|undefined} ldsSupplement
 * @returns {Array<{ name: string, priceIls: number }>}
 */
export function buildHotelsRiverPackageQuotePdfSupplementsList(ldsSupplement) {
  if (!ldsSupplement || typeof ldsSupplement !== "object") return [];
  const entries = Array.isArray(ldsSupplement)
    ? ldsSupplement
    : Object.values(ldsSupplement);
  const lines = [];
  for (const supp of entries) {
    if (!supp || typeof supp !== "object" || !supp.checked) continue;
    const priceIls = Number(supp.ourFare ?? supp.fare ?? 0);
    if (!Number.isFinite(priceIls) || priceIls <= 0) continue;
    const typeLabel = hebrewBaggageTypeLabel(supp);
    const weight = baggageWeightText(supp);
    const direction = baggageFlightDirectionSuffix(supp);
    const name = weight
      ? `${typeLabel} ${weight}${direction}`
      : `${typeLabel}${direction}`;
    lines.push({
      name,
      priceIls: Math.round(priceIls * 100) / 100,
    });
  }
  return lines;
}

/**
 * Map HotelsRiver DP marketing offer view-model → vacation-package quote PDF fields.
 * Payment total comes only from opts.totalPriceIls (sidebar SSOT) — never from
 * viewModel.price.amountDisplay (often hotel-only packagePriceRaw).
 * Selected paid baggage comes from opts.ldsSupplement → supplementsList (תוספות).
 *
 * @param {object|null} viewModel
 * @param {{
 *   totalPriceIls?: number|null,
 *   customerName?: string,
 *   ilsToUsd?: Function|null,
 *   ldsSupplement?: object|null,
 * }} [opts]
 */
export function mapHotelsRiverPackageQuotePdfFields(viewModel, opts = {}) {
  if (!viewModel || viewModel.version !== 1) return null;

  const hotelName = viewModel.hotel?.name || "";
  const boardDetails =
    viewModel.hotel?.boardLabel && viewModel.hotel.boardLabel !== "—"
      ? viewModel.hotel.boardLabel
      : "";
  const hotelCity = viewModel.hotel?.locationLine || "";

  const depDate =
    displayDateFromVm(viewModel.flights?.outbound?.calendarDate) ||
    displayDateFromVm(viewModel.header?.checkIn);
  const retDate =
    displayDateFromVm(viewModel.flights?.inbound?.calendarDate) ||
    displayDateFromVm(viewModel.header?.checkOut);

  const roomsInfo = [];
  if (viewModel.room?.name) {
    roomsInfo.push({
      roomName: viewModel.room.name,
      board: boardDetails || undefined,
      occupancy: viewModel.room.occupancyLabel || undefined,
    });
  }

  const flightInfo = [];
  const out = viewModel.flights?.outbound;
  if (out?.hasLeg) {
    const route = parseMarketingOfferRouteLine(out.routeLine);
    const times = parseMarketingOfferTimeRange(out.timeRange);
    flightInfo.push({
      type: "יציאה",
      depCity: route.dep,
      arrCity: route.arr,
      depAirport: "",
      arrAirport: "",
      airline: "",
      airlineName: out.airline || "",
      airlineLogo: null,
      flightNumber: "",
      depDate: displayDateFromVm(out.calendarDate) || "",
      depTime: times.depTime,
      arrDate: "",
      arrTime: times.arrTime,
      // Included allowance only — paid bags go to supplementsList.
      baggageLabel: viewModel.flights?.baggageLine || null,
    });
  }
  const inn = viewModel.flights?.inbound;
  if (inn?.hasLeg) {
    const route = parseMarketingOfferRouteLine(inn.routeLine);
    const times = parseMarketingOfferTimeRange(inn.timeRange);
    flightInfo.push({
      type: "חזרה",
      depCity: route.dep,
      arrCity: route.arr,
      depAirport: "",
      arrAirport: "",
      airline: "",
      airlineName: inn.airline || "",
      airlineLogo: null,
      flightNumber: "",
      depDate: displayDateFromVm(inn.calendarDate) || "",
      depTime: times.depTime,
      arrDate: "",
      arrTime: times.arrTime,
      baggageLabel: null,
    });
  }

  const totalFromOpts = Number(opts.totalPriceIls);
  // Never fall back to viewModel.price.amountDisplay — that field is fed by
  // hotel-only packagePriceRaw (~₪1,213) and is not the package payment total.
  const totalPrice =
    Number.isFinite(totalFromOpts) && totalFromOpts > 0
      ? Math.round(totalFromOpts)
      : 0;

  const greeting = String(opts.customerName || "").trim();
  const quoteRefLine = greeting
    ? `<div class="ref">עבור: ${escapeHtml(greeting)}</div>`
    : "";

  const supplementsList = buildHotelsRiverPackageQuotePdfSupplementsList(
    opts.ldsSupplement
  );

  return {
    hotelName,
    boardDetails,
    hotelCity,
    depDate,
    retDate,
    roomsInfo,
    flightInfo,
    supplementsList,
    totalPrice,
    ilsToUsd: typeof opts.ilsToUsd === "function" ? opts.ilsToUsd : null,
    includeAgentSectionInPdf: false,
    quoteRefLine,
    titleHe: "הצעת מחיר - חבילת נופש",
    titleSuffix: greeting || hotelName || "quote",
    fileSlug: greeting || hotelName || "quote",
  };
}
